import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PasswordInput } from "@/components/PasswordInput";

export default function Account() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setErr("New passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/me/password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="account-page">
      <header>
        <p className="hh-overline mb-3">Account</p>
        <h1 className="font-serif text-4xl text-hh-ink tracking-tight">Your password</h1>
        <p className="mt-3 text-hh-ink2 max-w-xl text-base">
          Choose something you&rsquo;ll remember. We&rsquo;ll never email you about anything other than your royalties.
        </p>
      </header>

      <form onSubmit={onSubmit} className="hh-card p-8 md:p-10 max-w-lg space-y-5">
        <div className="flex items-start gap-3 mb-4">
          <span className="h-9 w-9 rounded-full bg-hh-muted grid place-items-center shrink-0">
            <KeyRound size={15} className="text-hh-primary" strokeWidth={1.5} />
          </span>
          <div>
            <h2 className="font-serif text-lg text-hh-ink">Change your password</h2>
            <p className="text-xs text-hh-ink2 mt-0.5">Active immediately. You&rsquo;ll stay signed in on this device.</p>
          </div>
        </div>
        <Field label="Current password" type="password" testid="pw-current" autoComplete="current-password"
               value={current} onChange={setCurrent} />
        <Field label="New password" type="password" testid="pw-new" autoComplete="new-password"
               value={next} onChange={setNext} minLength={8} />
        <Field label="Confirm new password" type="password" testid="pw-confirm" autoComplete="new-password"
               value={confirm} onChange={setConfirm} minLength={8} />
        {err && (
          <div data-testid="pw-error" className="text-sm text-hh-danger bg-[#FBEAE6] border border-[#F1CEC5] rounded-lg px-4 py-3">{err}</div>
        )}
        <div className="flex gap-3 justify-end pt-2">
          <button type="button" onClick={() => navigate(-1)} className="hh-btn-ghost">Cancel</button>
          <button type="submit" disabled={loading} data-testid="pw-submit" className="hh-btn-primary disabled:opacity-60">
            {loading ? <Loader2 size={14} className="animate-spin mr-1.5 inline" /> : null}
            Save new password
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, autoComplete, minLength }) {
  const isPwd = type === "password";
  const InputCmp = isPwd ? PasswordInput : "input";
  return (
    <div>
      <label className="block text-sm text-hh-ink2 mb-2">{label}</label>
      <InputCmp
        {...(isPwd ? {} : { type })}
        required minLength={minLength} autoComplete={autoComplete}
        value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink focus:outline-none focus:ring-2 focus:ring-hh-primary/40 focus:border-hh-primary transition-all"
      />
    </div>
  );
}
